<template>
  <PropertyOnboardingCard :wide="wide" v-if="!isLoading">
    <template #header>
      <Stepper :active-step-index="currentStepIndex" :total-steps="steps.length" @set-step="setStep">
        <template #stepper-action-button>
          <Button variant="ghost" @click="saveAndExit" :loading="isSavingAndExit" :disabled="isSaving">
            {{ property ? 'Quit & save' : 'Exit' }}
          </Button>
        </template>
      </Stepper>
    </template>

    <template #body>
      <div class="content" :class="{ 'full-width': fullWidth }">
        <Text variant="h1" v-if="props.title">{{ props.title }}</Text>
        <div class="form">
          <slot />
        </div>
      </div>
    </template>

    <template #footer>
      <Button variant="ghost" @click="back" v-if="isVisibleBackButton">
        <Icon name="chevron-left" />
        <span>Back</span>
      </Button>
      <Button
        @click="saveAndNext"
        :loading="isSaving"
        :disabled="isSavingAndExit || props.isNextButtonDisabled"
        class="next-button"
      >
        {{ props.nextButtonText ? props.nextButtonText : 'Next' }}
      </Button>
    </template>
  </PropertyOnboardingCard>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { Button, Icon, Stepper, Text } from '@tallo/design-system';
import { useAvailabilityStore } from '@tallo/investor/availability';
import { AppRouteNames } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import { PropertyOnboardingStage, PropertyOnboardingStep } from '../configs/stages.enum';
import { usePropertyOnboardingStore } from '../store/property-onboarding.store';
import PropertyOnboardingCard from './property-onboarding-card.vue';

interface PropertyOnboardingStepProps {
  title?: string;
  submit: () => Promise<any>;
  isNextButtonDisabled?: boolean;
  nextButtonText?: string;
  fullWidth?: boolean;
  wide?: boolean;
}

const route = useRoute();
const router = useRouter();
const props = defineProps<PropertyOnboardingStepProps>();
const propertyOnboardingStore = usePropertyOnboardingStore();
const availabilityStore = useAvailabilityStore();
const { isLoading, property } = storeToRefs(usePropertyStore());
const { getStageSteps, isStepCompleted } = storeToRefs(propertyOnboardingStore);

const isSaving = ref(false);
const isSavingAndExit = ref(false);
const currentStageName = computed(() => route.meta.stageName as PropertyOnboardingStage);
const currentStepName = computed(() => route.meta.stepName as PropertyOnboardingStep);
const steps = computed(() => getStageSteps.value(currentStageName.value));
const currentStepIndex = computed(() => steps.value.findIndex((step) => step === currentStepName.value));
const isVisibleBackButton = computed(() => {
  const isFirstStage = propertyOnboardingStore.isFirstStage(currentStageName.value);
  const isFirstStep = propertyOnboardingStore.isFirstStep(currentStageName.value, currentStepName.value);

  return isFirstStage ? !isFirstStep : true;
});

async function saveAndNext() {
  await save(isSaving);

  const propertyId = property.value?.id;
  const nextStep = propertyOnboardingStore.getNextStep(currentStageName.value, currentStepName.value);
  const isLastStep = propertyOnboardingStore.isLastStep(currentStageName.value, currentStepName.value);
  const isLastStage = propertyOnboardingStore.isLastStage(currentStageName.value);

  if (isLastStep && isLastStage) {
    const routeName = !availabilityStore.getAvailabilitySlots.length
      ? AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_AVAILABILITY
      : AppRouteNames.INVESTOR_PROPERTY_DETAILS;

    return router.push({
      name: routeName,
      params: {
        propertyId,
      },
    });
  }

  if (isLastStep) {
    return router.push({
      name: AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_OVERVIEW,
      params: {
        propertyId,
      },
    });
  }

  propertyOnboardingStore.navigateToStep(nextStep);
}

async function saveAndExit() {
  if (property.value) {
    await save(isSavingAndExit);
  }

  router.push('/');
}

async function save(loading: Ref<boolean>) {
  loading.value = true;

  try {
    await props.submit();
  } finally {
    loading.value = false;
  }
}

function back() {
  const prevStep = propertyOnboardingStore.getPrevStep(currentStageName.value, currentStepName.value);

  if (prevStep) {
    propertyOnboardingStore.navigateToStep(prevStep);
  } else {
    router.push({
      name: AppRouteNames.INVESTOR_PROPERTY_ONBOARDING_OVERVIEW,
      params: {
        propertyId: property.value?.id,
      },
    });
  }
}

function setStep(stepIndex: number) {
  const step = steps.value[stepIndex];
  const prevStep = steps.value[stepIndex - 1];

  if (prevStep && !isStepCompleted.value(prevStep)) {
    return;
  }

  router.push(step);
}
</script>

<style scoped lang="scss">
@import 'styles/shared/variables';
@import 'styles/mixins/layout.mixins';

.content {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);

  max-width: 32rem;
  margin: 0 auto;
  padding-block: var(--t-spacing-xxl);

  @include mobile {
    padding-block: var(--t-spacing-lg);
  }

  &.full-width {
    max-width: 100%;
  }
}

.next-button {
  margin-inline-start: auto;
}
</style>
